<template>
    <div class="event-picker">
        <input
            v-model="search"
            class="form-input"
            placeholder="Chercher un tournoi (nom, lieu)..."
            @focus="open = true"
        />
        <div v-if="open && filtered.length" class="event-picker-list">
            <button
                v-for="ev in filtered"
                type="button"
                class="event-picker-item"
                @click="select(ev)"
                :key="ev.id"
            >
                <span class="event-picker-name">{{ ev.display_name || ev.name }}</span>
                <span class="event-picker-meta">
                    {{ ev.date ?? 'date inconnue' }}<template v-if="ev.place"> — {{ ev.place }}</template>
                </span>
            </button>
        </div>
        <p v-else-if="open && search.length >= 2 && !filtered.length" class="hint">
            Aucun tournoi trouvé pour "{{ search }}".
        </p>
        <div v-if="selected" class="event-picker-selected">
            <div class="event-picker-selected-text">
                Sélectionné : <strong>{{ selected.name }}</strong>
                <span class="event-picker-meta">
                    {{ selected.date ?? 'date inconnue' }}<template v-if="selected.place"> — {{ selected.place }}</template>
                </span>
            </div>
            <button type="button" class="event-picker-clear" @click="clear">×</button>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useEventStore } from '@/stores/useEventStore'
import { getSeasonFromDate } from '@/config/config'
import type { EventOption } from './types'

const props = defineProps<{
    modelValue: number | null
    // Restreint la liste au calendrier d'un comité/d'une ligue précis (rep comité/ligue,
    // "leur propre calendrier" -- cf. discussion du 2026-09-20) -- absent/null = pas de
    // filtre, réservé au super_admin qui peut importer n'importe où.
    scopeFilter?: { level: 'comite' | 'ligue'; code: string } | null
    // Label de la saison choisie dans le wizard (ex. "2026-2027") -- sans ce filtre, un
    // tournoi d'une AUTRE saison pouvait apparaître dans la recherche alors que la saison
    // sélectionnée juste au-dessus ne correspond pas (bug constaté le 2026-09-20). `null` =
    // pas de filtre (saison pas encore choisie).
    seasonLabel?: string | null
}>()
const emit = defineEmits<{
    'update:modelValue': [value: number | null]
    // Événement complet (date incluse) au moment de la sélection/effacement -- permet au
    // parent (StepParams) de préremplir automatiquement la date du tournoi plutôt que de
    // forcer une ressaisie manuelle alors qu'elle est déjà connue.
    select: [event: EventOption | null]
}>()

const eventStore = useEventStore()
const search = ref('')
const open = ref(false)
const selected = ref<EventOption | null>(null)

onMounted(async () => {
    if (!eventStore.events.length) await eventStore.fetchEvents('officiel')
    if (props.modelValue) {
        const found = eventStore.events.find((e: any) => e.id === props.modelValue)
        if (found) {
            selected.value = toOption(found)
            // Ne pas ré-émettre 'select' ici : au montage avec un modelValue déjà présent
            // (retour depuis l'étape suivante), la date a déjà pu être ajustée manuellement
            // par l'admin -- ne pas l'écraser silencieusement.
        }
    }
})

function toOption(e: any): EventOption {
    return {
        id: e.id,
        name: e.name,
        display_name: e.display_name ?? null,
        date: e.date ?? null,
        place: e.place ?? null,
        organiser: e.organiser ?? null,
        comite: e.comite ?? null,
        ligue: e.ligue ?? null,
    }
}

const normalize = (s: string) => s.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase()

const filtered = computed(() => {
    if (search.value.trim().length < 2) return []
    const q = normalize(search.value)
    return eventStore.events
        .filter((e: any) => normalize(e.display_name || e.name).includes(q) || normalize(e.place ?? '').includes(q))
        .filter((e: any) => {
            if (!props.scopeFilter) return true
            const code = props.scopeFilter.level === 'comite' ? e.comite : e.ligue
            return code === props.scopeFilter.code
        })
        .filter((e: any) => !props.seasonLabel || !e.date || getSeasonFromDate(e.date) === props.seasonLabel)
        .slice(0, 20)
        .map(toOption)
})

function select(ev: EventOption) {
    selected.value = ev
    search.value = ''
    open.value = false
    emit('update:modelValue', ev.id)
    emit('select', ev)
}

function clear() {
    selected.value = null
    emit('update:modelValue', null)
    emit('select', null)
}
</script>

<style scoped>
.event-picker {
    position: relative;
}

.event-picker-list {
    position: absolute;
    z-index: 20;
    top: 100%;
    left: 0;
    right: 0;
    max-width: 100%;
    max-height: 260px;
    overflow-y: auto;
    overflow-x: hidden;
    background: var(--color-bg, #fff);
    border: 1px solid var(--color-light-shadow, #ddd);
    border-radius: var(--radius, 8px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
    box-sizing: border-box;
}

.event-picker-item {
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    text-align: left;
    padding: 0.5rem 0.75rem;
    border: none;
    background: transparent;
    cursor: pointer;
    overflow: hidden;
}

.event-picker-item:hover {
    background: var(--color-bg-soft, rgba(0, 0, 0, 0.05));
}

.event-picker-name,
.event-picker-meta {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.event-picker-name {
    font-weight: 600;
}

.event-picker-meta {
    font-size: 0.8rem;
    color: var(--color-fg-muted, #888);
}

.event-picker-selected {
    margin-top: 0.4rem;
    font-size: 0.9rem;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.5rem;
}

.event-picker-selected-text {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.event-picker-selected-text .event-picker-meta {
    white-space: normal;
}

.event-picker-clear {
    flex: none;
    border: none;
    background: transparent;
    cursor: pointer;
    font-size: 1rem;
    color: var(--color-fg-muted, #888);
}

.hint {
    font-size: 0.85rem;
    color: var(--color-fg-muted, #888);
    margin: 0.25rem 0 0;
}
</style>
